import React, { useCallback, useEffect, useState } from "react";
import {
    Radar,
    Users as UsersIcon,
    Eye,
    Globe2,
    Smartphone,
    Monitor,
    Tablet,
    ExternalLink,
    RefreshCw,
    Wifi,
    Activity,
    History,
    TrendingDown,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { trafficOverview, trafficLive, trafficSessions } from "@/lib/api";

const FLAG_OFFSET = 127397;
function flagOf(cc) {
    if (!cc || cc.length !== 2) return "🌐";
    return cc
        .toUpperCase()
        .split("")
        .map((ch) => String.fromCodePoint(ch.charCodeAt(0) + FLAG_OFFSET))
        .join("");
}

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 60) return `hace ${Math.round(diff)}s`;
        if (diff < 3600) return `hace ${Math.round(diff / 60)} min`;
        if (diff < 86400) return `hace ${Math.round(diff / 3600)} h`;
        return `hace ${Math.round(diff / 86400)} d`;
    } catch {
        return iso;
    }
}

function fmtSecs(s) {
    if (!s) return "0s";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return ss ? `${m}m ${ss}s` : `${m}m`;
}

const DeviceIcon = ({ device, className = "" }) => {
    if (device === "mobile")
        return <Smartphone size={14} className={className} />;
    if (device === "tablet") return <Tablet size={14} className={className} />;
    return <Monitor size={14} className={className} />;
};

function KpiCard({ label, value, hint, accent, testid }) {
    return (
        <div
            className={`rounded-2xl p-5 border-2 ${accent} bg-white shadow-pop`}
            data-testid={testid}
        >
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70 mb-1">
                {label}
            </div>
            <div className="font-display text-4xl font-black tracking-tight leading-none">
                {value}
            </div>
            {hint && (
                <div className="text-xs font-medium opacity-60 mt-2">{hint}</div>
            )}
        </div>
    );
}

function Bar({ name, count, total, color = "bg-brand-600" }) {
    const pct = total ? Math.max(2, Math.round((count / total) * 100)) : 0;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-ink-700 truncate pr-2">{name}</span>
                <span className="font-mono font-bold text-ink-950 shrink-0">
                    {count.toLocaleString("es-ES")}
                </span>
            </div>
            <div className="h-2 bg-ink-100 rounded-full overflow-hidden">
                <div
                    className={`${color} h-full rounded-full transition-all`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function FunnelBlock({ funnel }) {
    if (!funnel?.length) return null;
    const top = funnel[0]?.count || 0;
    const endToEnd =
        top > 0
            ? `${((funnel[funnel.length - 1].count / top) * 100).toFixed(1)} %`
            : "—";
    return (
        <div
            className="bg-gradient-to-br from-violet-50 via-fuchsia-50 to-white border border-violet-200 rounded-2xl p-6 shadow-pop"
            data-testid="funnel-block"
        >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <TrendingDown size={16} className="text-violet-600" />
                    <h2 className="font-display text-lg font-black text-ink-950">
                        Embudo de conversión · 7 días
                    </h2>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700 bg-white px-2 py-1 rounded-full border border-violet-200">
                    {endToEnd} end-to-end
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {funnel.map((step, idx) => {
                    const pctOfTop = top ? Math.round((step.count / top) * 100) : 0;
                    const prevCount = idx > 0 ? funnel[idx - 1].count : null;
                    const stepConv =
                        prevCount > 0
                            ? `${((step.count / prevCount) * 100).toFixed(1)} %`
                            : null;
                    return (
                        <div
                            key={step.label}
                            className="bg-white rounded-2xl p-4 border border-violet-100"
                            data-testid={`funnel-step-${idx}`}
                        >
                            <div className="text-[10px] font-bold uppercase tracking-wider text-ink-500 mb-1">
                                Paso {idx + 1}
                            </div>
                            <div className="text-sm font-bold text-ink-950 leading-tight mb-2">
                                {step.label}
                            </div>
                            <div className="font-display text-3xl font-black text-violet-700">
                                {step.count.toLocaleString("es-ES")}
                            </div>
                            <div className="mt-3 h-2 bg-violet-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-full transition-all"
                                    style={{ width: `${Math.max(2, pctOfTop)}%` }}
                                />
                            </div>
                            <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono">
                                <span className="text-ink-400">{pctOfTop} % del total</span>
                                {stepConv && (
                                    <span
                                        className="font-bold text-emerald-700"
                                        title="Conversión vs. paso anterior"
                                    >
                                        ↓ {stepConv}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function HistoryTab() {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        trafficSessions(days, 200)
            .then((d) => {
                if (alive) {
                    setData(d);
                    setError("");
                }
            })
            .catch((e) => {
                if (alive)
                    setError(e?.response?.data?.detail || "No se pudo cargar el historial");
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [days]);

    return (
        <div className="bg-white border border-ink-100 rounded-2xl shadow-pop overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-ink-100 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <History size={16} className="text-brand-600" />
                    <h2 className="font-display text-lg font-black text-ink-950">
                        Historial de visitas
                    </h2>
                    {data && (
                        <span className="text-xs font-mono text-ink-400">
                            {data.count} sesion{data.count === 1 ? "" : "es"}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {[1, 7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${
                                days === d
                                    ? "bg-brand-600 text-white"
                                    : "bg-ink-50 text-ink-600 hover:bg-ink-100"
                            }`}
                            data-testid={`hist-days-${d}`}
                        >
                            {d === 1 ? "Hoy" : `${d} días`}
                        </button>
                    ))}
                </div>
            </div>
            {error && (
                <div className="bg-red-50 border-y border-red-200 text-red-800 p-3 text-sm">
                    {error}
                </div>
            )}
            {loading && (
                <div className="p-8 text-sm text-ink-400 text-center">Cargando…</div>
            )}
            {!loading && data && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="history-table">
                        <thead className="bg-ink-50/60 text-[10px] font-bold uppercase tracking-wider text-ink-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Fecha</th>
                                <th className="px-4 py-3 text-left">País</th>
                                <th className="px-4 py-3 text-left">Página</th>
                                <th className="px-4 py-3 text-left">Origen</th>
                                <th className="px-4 py-3 text-left">Dispositivo</th>
                                <th className="px-4 py-3 text-right">Vistas</th>
                                <th className="px-4 py-3 text-left">Duración</th>
                                <th className="px-4 py-3 text-left">Campaña</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ink-100">
                            {data.sessions.map((s) => {
                                let duration = 0;
                                try {
                                    duration = Math.max(
                                        0,
                                        Math.round(
                                            (new Date(s.last_seen).getTime() -
                                                new Date(s.first_seen).getTime()) /
                                                1000,
                                        ),
                                    );
                                } catch {
                                    /* ignore */
                                }
                                const utmCampaign = s.utm?.campaign || "";
                                return (
                                    <tr
                                        key={s.session_id}
                                        className="hover:bg-ink-50/40"
                                        data-testid={`history-row-${s.session_id}`}
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-ink-950 font-medium">
                                                {new Date(s.first_seen).toLocaleString(
                                                    "es-ES",
                                                    {
                                                        day: "2-digit",
                                                        month: "short",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    },
                                                )}
                                            </div>
                                            <div className="text-[10px] text-ink-400 font-mono">
                                                {fmtRel(s.first_seen)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-lg">{flagOf(s.country)}</span>
                                            <span className="ml-1 font-mono text-xs">
                                                {s.country || "—"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-ink-950 font-medium truncate max-w-[200px]">
                                                {s.last_category || "—"}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                                    s.referrer_source === "Google"
                                                        ? "bg-amber-50 text-amber-700"
                                                        : s.referrer_source === "Directo"
                                                          ? "bg-ink-50 text-ink-600"
                                                          : "bg-violet-50 text-violet-700"
                                                }`}
                                            >
                                                {s.referrer_source || "Directo"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-1 text-ink-700 text-xs">
                                                <DeviceIcon device={s.device} />
                                                {s.browser}
                                                <span className="text-ink-400">· {s.os}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono font-bold">
                                            {s.page_view_count || 1}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-ink-600 whitespace-nowrap">
                                            {fmtSecs(duration)}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {utmCampaign ? (
                                                <span className="font-bold text-violet-700">
                                                    {utmCampaign}
                                                </span>
                                            ) : (
                                                <span className="text-ink-300">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!data.sessions.length && (
                                <tr>
                                    <td
                                        colSpan={8}
                                        className="px-4 py-12 text-center text-sm text-ink-400"
                                    >
                                        Sin visitas en este periodo.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}


function HourlyChart({ data }) {
    if (!data || !data.length) {
        return (
            <div className="text-sm text-ink-400 font-medium">
                Sin actividad en las últimas 24 horas todavía.
            </div>
        );
    }
    const max = Math.max(...data.map((d) => d.count), 1);
    // Build a 24-hour buckets array
    const now = new Date();
    const buckets = [];
    for (let i = 23; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 3600 * 1000);
        const key = d.toISOString().slice(0, 13);
        const item = data.find((x) => x.hour === key);
        buckets.push({
            label: d.toLocaleTimeString("es-ES", { hour: "2-digit" }),
            count: item?.count || 0,
        });
    }
    return (
        <div className="flex items-end gap-1 h-32 px-1">
            {buckets.map((b, i) => {
                const h = (b.count / max) * 100;
                return (
                    <div
                        key={i}
                        className="flex-1 flex flex-col items-center gap-1 group cursor-default"
                        title={`${b.label} — ${b.count} visitas`}
                    >
                        <div className="flex-1 w-full flex items-end">
                            <div
                                className="w-full bg-gradient-to-t from-brand-600 to-cyan-500 rounded-t-md group-hover:opacity-80 transition"
                                style={{ height: `${h}%`, minHeight: b.count ? "4px" : "0px" }}
                            />
                        </div>
                        {i % 3 === 0 && (
                            <span className="text-[9px] text-ink-400 font-bold">
                                {b.label.replace(":", "")}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default function Visitas() {
    const { user } = useAuth();
    const [overview, setOverview] = useState(null);
    const [live, setLive] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [tab, setTab] = useState("resumen"); // resumen | historial

    const load = useCallback(async () => {
        try {
            const [ov, lv] = await Promise.all([trafficOverview(), trafficLive()]);
            setOverview(ov);
            setLive(lv);
            setError("");
        } catch (e) {
            setError(e?.response?.data?.detail || "No se pudo cargar las visitas");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 5000); // refresh every 5s
        return () => clearInterval(id);
    }, [load]);

    if (!user?.is_admin) {
        return (
            <Layout>
                <div className="p-8" data-testid="visitas-blocked">
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 max-w-xl">
                        <div className="font-bold text-amber-700 mb-1">
                            Acceso restringido
                        </div>
                        <p className="text-sm text-amber-800">
                            Esta página es del administrador del SaaS.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 md:p-8 space-y-6" data-testid="visitas-page">
                {/* Header */}
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-brand-700 mb-2">
                            <Radar size={18} />
                            <span className="text-[10px] font-bold uppercase tracking-[0.22em]">
                                Tráfico web en vivo
                            </span>
                        </div>
                        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-ink-950">
                            Visitas a ailicitaurcal.com
                        </h1>
                        <p className="text-sm text-ink-500 font-medium mt-1">
                            Actualización automática cada 5 segundos · sin cookies · GDPR-friendly
                        </p>
                    </div>
                    <Button
                        onClick={load}
                        variant="outline"
                        className="rounded-full gap-1.5 border-brand-200 hover:bg-brand-50"
                        data-testid="visitas-refresh"
                    >
                        <RefreshCw size={13} />
                        Actualizar
                    </Button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
                        {error}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b border-ink-100">
                    {[
                        { id: "resumen", label: "Resumen en vivo", icon: Activity },
                        { id: "historial", label: "Historial", icon: History },
                    ].map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 transition-colors -mb-px ${
                                tab === id
                                    ? "border-brand-600 text-brand-700"
                                    : "border-transparent text-ink-500 hover:text-ink-950"
                            }`}
                            data-testid={`visitas-tab-${id}`}
                        >
                            <Icon size={14} />
                            {label}
                        </button>
                    ))}
                </div>

                {tab === "historial" && <HistoryTab />}

                {tab === "resumen" && loading && !overview && (
                    <div className="text-sm text-ink-400 font-medium">Cargando…</div>
                )}

                {tab === "resumen" && overview && (
                    <>
                        {/* KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <KpiCard
                                label="Online ahora"
                                value={
                                    <span className="flex items-center gap-2">
                                        <span className="relative flex h-3 w-3">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                                        </span>
                                        {overview.online_now}
                                    </span>
                                }
                                hint="Activos en los últimos 45s"
                                accent="border-emerald-200"
                                testid="kpi-online"
                            />
                            <KpiCard
                                label="Visitas hoy"
                                value={overview.unique_visitors_today.toLocaleString(
                                    "es-ES",
                                )}
                                hint={`${overview.sessions_today} sesiones · ${overview.page_views_today} páginas vistas`}
                                accent="border-brand-200"
                                testid="kpi-today"
                            />
                            <KpiCard
                                label="Sesiones 7 días"
                                value={overview.sessions_7d.toLocaleString("es-ES")}
                                hint="Última semana"
                                accent="border-violet-200"
                                testid="kpi-7d"
                            />
                            <KpiCard
                                label="Páginas vistas hoy"
                                value={overview.page_views_today.toLocaleString(
                                    "es-ES",
                                )}
                                hint={
                                    overview.sessions_today
                                        ? `${(
                                              overview.page_views_today /
                                              overview.sessions_today
                                          ).toFixed(1)} por sesión`
                                        : "—"
                                }
                                accent="border-amber-200"
                                testid="kpi-pageviews"
                            />
                            <KpiCard
                                label="Países (7d)"
                                value={overview.top_countries.length}
                                hint={overview.top_countries
                                    .slice(0, 4)
                                    .map((c) => `${flagOf(c.name)} ${c.count}`)
                                    .join("  ")}
                                accent="border-cyan-200"
                                testid="kpi-countries"
                            />
                        </div>

                        {/* Conversion funnel */}
                        <FunnelBlock funnel={overview.funnel} />

                        {/* Hourly chart + Live list */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 bg-white border border-ink-100 rounded-2xl p-6 shadow-pop">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <Activity size={16} className="text-brand-600" />
                                        <h2 className="font-display text-lg font-black text-ink-950">
                                            Últimas 24 horas
                                        </h2>
                                    </div>
                                    <span className="text-xs font-mono text-ink-500">
                                        {overview.sessions_today} sesiones hoy
                                    </span>
                                </div>
                                <HourlyChart data={overview.hourly_24h} />
                            </div>

                            <div className="bg-white border border-ink-100 rounded-2xl p-6 shadow-pop">
                                <div className="flex items-center gap-2 mb-4">
                                    <Wifi size={16} className="text-emerald-600" />
                                    <h2 className="font-display text-lg font-black text-ink-950">
                                        En línea ahora
                                    </h2>
                                    <span
                                        className="ml-auto px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold"
                                        data-testid="live-count"
                                    >
                                        {live?.count || 0}
                                    </span>
                                </div>
                                {!live?.online?.length ? (
                                    <p className="text-sm text-ink-400 font-medium">
                                        Nadie navegando ahora mismo.
                                    </p>
                                ) : (
                                    <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
                                        {live.online.map((s) => (
                                            <li
                                                key={s.session_id}
                                                className="flex items-start gap-3 text-sm border-b border-ink-100 pb-3 last:border-0"
                                                data-testid={`live-session-${s.session_id}`}
                                            >
                                                <span className="text-xl shrink-0 mt-0.5">
                                                    {flagOf(s.country)}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-bold text-ink-950 truncate">
                                                        {s.last_category || s.last_path}
                                                    </div>
                                                    <div className="text-[11px] text-ink-500 flex items-center gap-1.5 flex-wrap">
                                                        <DeviceIcon
                                                            device={s.device}
                                                            className="text-ink-400"
                                                        />
                                                        {s.browser} · {s.os}
                                                        {s.referrer_source &&
                                                            s.referrer_source !== "Directo" && (
                                                                <span className="ml-1">
                                                                    ← {s.referrer_source}
                                                                </span>
                                                            )}
                                                    </div>
                                                    <div className="text-[10px] text-ink-400 font-mono mt-0.5">
                                                        Sesión {fmtSecs(s.session_seconds || 0)} ·{" "}
                                                        {s.page_view_count || 1} páginas · visto{" "}
                                                        {fmtRel(s.last_seen)}
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>

                        {/* Breakdown grids */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop">
                                <h3 className="font-display text-base font-black text-ink-950 mb-3 flex items-center gap-2">
                                    <Eye size={14} className="text-brand-600" />
                                    Top páginas (hoy)
                                </h3>
                                <div className="space-y-2.5">
                                    {overview.top_pages_today.slice(0, 6).map((p) => (
                                        <Bar
                                            key={p.name}
                                            name={p.name}
                                            count={p.count}
                                            total={overview.top_pages_today[0]?.count}
                                        />
                                    ))}
                                    {!overview.top_pages_today.length && (
                                        <div className="text-xs text-ink-400">
                                            Nada todavía hoy.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop">
                                <h3 className="font-display text-base font-black text-ink-950 mb-3 flex items-center gap-2">
                                    <Globe2 size={14} className="text-emerald-600" />
                                    Países (7d)
                                </h3>
                                <div className="space-y-2.5">
                                    {overview.top_countries.slice(0, 6).map((c) => (
                                        <Bar
                                            key={c.name}
                                            name={`${flagOf(c.name)}  ${c.name || "—"}`}
                                            count={c.count}
                                            total={overview.top_countries[0]?.count}
                                            color="bg-emerald-600"
                                        />
                                    ))}
                                    {!overview.top_countries.length && (
                                        <div className="text-xs text-ink-400">Sin datos.</div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop">
                                <h3 className="font-display text-base font-black text-ink-950 mb-3 flex items-center gap-2">
                                    <ExternalLink size={14} className="text-violet-600" />
                                    Cómo te encuentran (7d)
                                </h3>
                                <div className="space-y-2.5">
                                    {overview.top_referrers.slice(0, 6).map((r) => (
                                        <Bar
                                            key={r.name}
                                            name={r.name}
                                            count={r.count}
                                            total={overview.top_referrers[0]?.count}
                                            color="bg-violet-600"
                                        />
                                    ))}
                                    {!overview.top_referrers.length && (
                                        <div className="text-xs text-ink-400">Sin datos.</div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop">
                                <h3 className="font-display text-base font-black text-ink-950 mb-3 flex items-center gap-2">
                                    <UsersIcon size={14} className="text-amber-600" />
                                    Dispositivos & navegadores
                                </h3>
                                <div className="space-y-2.5">
                                    {overview.devices.map((d) => (
                                        <Bar
                                            key={d.name}
                                            name={d.name === "mobile" ? "📱 Móvil" : d.name === "tablet" ? "📋 Tablet" : "🖥️ Escritorio"}
                                            count={d.count}
                                            total={overview.devices.reduce(
                                                (a, b) => a + b.count,
                                                0,
                                            )}
                                            color="bg-amber-500"
                                        />
                                    ))}
                                    <div className="h-px bg-ink-100 my-3" />
                                    {overview.browsers.slice(0, 4).map((b) => (
                                        <Bar
                                            key={b.name}
                                            name={b.name}
                                            count={b.count}
                                            total={overview.browsers[0]?.count}
                                            color="bg-ink-700"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Campaigns (only if any) */}
                        {overview.top_campaigns?.length > 0 && (
                            <div className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop">
                                <h3 className="font-display text-base font-black text-ink-950 mb-3">
                                    Campañas UTM (7d)
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {overview.top_campaigns.map((c) => (
                                        <div
                                            key={c.name}
                                            className="rounded-xl border border-violet-100 bg-violet-50 p-3"
                                        >
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-0.5">
                                                Campaña
                                            </div>
                                            <div className="font-bold text-ink-950 text-sm truncate">
                                                {c.name}
                                            </div>
                                            <div className="font-mono font-black text-2xl text-violet-700 mt-1">
                                                {c.count}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pages 7d */}
                        <div className="bg-white border border-ink-100 rounded-2xl p-5 shadow-pop">
                            <h3 className="font-display text-base font-black text-ink-950 mb-3">
                                Top páginas (últimos 7 días)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
                                {overview.top_pages_7d.slice(0, 10).map((p) => (
                                    <Bar
                                        key={p.name}
                                        name={p.name}
                                        count={p.count}
                                        total={overview.top_pages_7d[0]?.count}
                                    />
                                ))}
                            </div>
                        </div>

                        <p className="text-xs text-ink-400 text-center">
                            Última actualización: {fmtRel(overview.generated_at)} · sin
                            cookies, IPs anonimizadas con hash diario
                        </p>
                    </>
                )}
            </div>
        </Layout>
    );
}
